import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../api/baseQuery';

export const paymentApi = createApi({
    reducerPath: 'paymentApi',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Payment', 'Category'],
    endpoints: (builder) => ({
        // Get all payments
        getPayments: builder.query({
            query: (params = {}) => {
                const { page = 0, pageSize = 8, keyword = '', status = '', categoryId = '' } = params;
                let url = `/payment/list?page=${page}&pageSize=${pageSize}`;
                
                if (keyword.trim()) {
                    url += `&keyword=${encodeURIComponent(keyword.trim())}`;
                }
                if (status.trim()) {
                    url += `&status=${encodeURIComponent(status)}`;
                }
                if (categoryId.trim()) {
                    url += `&categoryId=${encodeURIComponent(categoryId)}`;
                }
                
                return {
                    url,
                    method: 'GET'
                };
            },
            transformResponse: (response) => {
                if (!response?.value) {
                    return {
                        payments: [],
                        totalCount: 0,
                        isSuccess: false
                    };
                }
                return {
                    payments: response.value.payments || [],
                    totalCount: response.value.totalCount || 0,
                    isSuccess: response.isSuccess
                };
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'An error occurred',
                errors: response.data?.errors || [],
                validationErrors: response.data?.validationErrors || []
            }),
            providesTags: ['Payment']
        }),

        // Get payment by ID
        getPaymentById: builder.query({
            query: (id) => ({
                url: `/payment/${id}`,
                method: 'GET'
            }),
            transformResponse: (response) => {
                if (!response?.value) {
                    return null;
                }
                return {
                    ...response.value,
                    isSuccess: response.isSuccess,
                    errors: response.errors || [],
                    validationErrors: response.validationErrors || []
                };
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Payment not found',
                errors: response.data?.errors || [],
                validationErrors: response.data?.validationErrors || []
            }),
            providesTags: (result, error, id) => [{ type: 'Payment', id }]
        }),

        // Get categories
        getCategories: builder.query({
            query: () => ({
                url: '/category/list',
                method: 'GET'
            }),
            transformResponse: (response) => {
                if (!response) {
                    return {
                        value: [],
                        isSuccess: false,
                        errors: [],
                        validationErrors: []
                    };
                }
                
                return {
                    value: response.value || [],
                    isSuccess: response.isSuccess,
                    errors: response.errors || [],
                    validationErrors: response.validationErrors || []
                };
            },
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to load categories',
                errors: response.data?.errors || []
            }),
            providesTags: ['Category']
        }),

        // Create payment
        createPayment: builder.mutation({
            query: (data) => ({
                url: '/payment/create',
                method: 'POST',
                body: {
                    name: data.name,
                    description: data.description || '',
                    logo: data.logo,
                    tenantIds: data.tenantIds || [],
                    categoryIds: data.categoryIds || [],
                    contactPerson: data.contactPerson || '',
                    email: data.email || '',
                    phone: data.phone || '',
                    website: data.website || '',
                    address: data.address || ''
                }
            }),
            transformResponse: (response) => {
                console.log('🔧 PaymentAPI CreatePayment - Raw Response:', response);
                console.log('📊 Response type:', typeof response);
                console.log('🔍 Response isSuccess:', response?.isSuccess);
                console.log('📋 Response status:', response?.status);
                console.log('⚠️ Response errors:', response?.errors);
                console.log('📝 Response validationErrors:', response?.validationErrors);
                
                const transformedResponse = {
                    ...response,
                    isSuccess: response?.isSuccess || response?.status === 0,
                    errors: response?.errors || [],
                    validationErrors: response?.validationErrors || []
                };
                
                console.log('✅ PaymentAPI CreatePayment - Transformed Response:', transformedResponse);
                return transformedResponse;
            },
            transformErrorResponse: (response) => {
                console.error('💥 PaymentAPI CreatePayment - Error Response:', response);
                console.error('📊 Error status:', response.status);
                console.error('📋 Error data:', response.data);
                
                const transformedError = {
                    status: response.status,
                    message: response.data?.message || 'Failed to create payment',
                    errors: response.data?.errors || ['An unexpected error occurred'],
                    validationErrors: response.data?.validationErrors || []
                };
                
                console.error('🔧 PaymentAPI CreatePayment - Transformed Error:', transformedError);
                return transformedError;
            },
            invalidatesTags: ['Payment']
        }),

        // Update payment
        updatePayment: builder.mutation({
            query: (data) => ({
                url: '/payment/update',
                method: 'PUT',
                body: {
                    id: data.id,
                    name: data.name,
                    description: data.description || '',
                    logo: data.logo,
                    tenantIds: data.tenantIds || [],
                    categoryIds: data.categoryIds || [],
                    contactPerson: data.contactPerson || '',
                    email: data.email || '',
                    phone: data.phone || '',
                    website: data.website || '',
                    address: data.address || ''
                }
            }),
            transformResponse: (response) => {
                console.log('🔧 PaymentAPI UpdatePayment - Raw Response:', response);
                console.log('📊 Response type:', typeof response);
                console.log('🔍 Response isSuccess:', response?.isSuccess);
                console.log('📋 Response status:', response?.status);
                console.log('⚠️ Response errors:', response?.errors);
                console.log('📝 Response validationErrors:', response?.validationErrors);
                
                const transformedResponse = {
                    ...response,
                    isSuccess: true,
                    errors: response?.errors || [],
                    validationErrors: response?.validationErrors || []
                };
                
                console.log('✅ PaymentAPI UpdatePayment - Transformed Response:', transformedResponse);
                return transformedResponse;
            },
            transformErrorResponse: (response) => {
                console.error('💥 PaymentAPI UpdatePayment - Error Response:', response);
                console.error('📊 Error status:', response.status);
                console.error('📋 Error data:', response.data);
                
                const transformedError = {
                    status: response.status,
                    message: response.data?.message || 'Failed to update payment',
                    errors: response.data?.errors || ['An unexpected error occurred'],
                    validationErrors: response.data?.validationErrors || []
                };
                
                console.error('🔧 PaymentAPI UpdatePayment - Transformed Error:', transformedError);
                return transformedError;
            },
            invalidatesTags: ['Payment']
        }),

        // Delete payment
        deletePayment: builder.mutation({
            query: (id) => ({
                url: `/payment/${id}`,
                method: 'DELETE'
            }),
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to delete payment',
                errors: response.data?.errors || []
            }),
            invalidatesTags: ['Payment']
        }),

        // Upload payment media
        uploadPaymentMedia: builder.mutation({
            query: ({ orderNo, file }) => {
                const formData = new FormData();
                formData.append('orderNo', orderNo);
                formData.append('file', file);
                return {
                    url: '/payment/media',
                    method: 'POST',
                    body: formData,
                    formData: true
                };
            },
            transformResponse: (response) => ({
                ...response,
                isSuccess: response?.isSuccess || response?.status === 0,
                errors: response?.errors || [],
                validationErrors: response?.validationErrors || []
            }),
            transformErrorResponse: (response) => ({
                status: response.status,
                message: response.data?.message || 'Failed to upload image',
                errors: response.data?.errors || ['Upload failed']
            })
        })
    })
});

export const {
    useGetPaymentsQuery,
    useGetPaymentByIdQuery,
    useCreatePaymentMutation,
    useUpdatePaymentMutation,
    useDeletePaymentMutation,
    useUploadPaymentMediaMutation,
    useGetCategoriesQuery
} = paymentApi; 